<template>
  <div class="doc-section">
    <div class="section-header">
      <span class="material-icons-sharp section-icon">warehouse</span>
      <h2>Gestion des Stocks</h2>
    </div>

    <p class="section-intro">
      Le module Stocks permet de suivre l'état des marchandises dans chaque magasin :
      consulter le stock en temps réel, enregistrer les entrées (réceptions de conteneurs)
      et les sorties de produits.
    </p>

    <!-- ===== CONSULTATION ===== -->
    <h3 class="sub-title">Consulter le stock d'un magasin</h3>
    <p class="section-text">
      Le tableau principal affiche tous les produits en stock avec les quantités disponibles.
      Cliquez sur une ligne pour voir les détails.
    </p>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ affiché</th><th>Description</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Produit</strong></td><td>Nom du produit (non modifiable).</td></tr>
          <tr><td><strong>Type</strong></td><td>Type / catégorie du produit (non modifiable).</td></tr>
          <tr><td><strong>Quantité en stock</strong></td><td>Quantité actuelle disponible (non modifiable).</td></tr>
        </tbody>
      </table>
    </div>

    <!-- ===== ENTRÉE ===== -->
    <h3 class="sub-title">Entrée de produit en stock</h3>
    <p class="section-text">
      L'entrée en stock concerne la réception effective d'un conteneur envoyé depuis l'Approvisionnement ou un ajustement manuel.
    </p>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Produit</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le produit à réceptionner.</td></tr>
          <tr><td><strong>Quantité (tonne)</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Poids total vérifié à la réception.</td></tr>
          <tr><td><strong>Quantité (unités)</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Nombre de sacs, cartons ou pièces réceptionnés.</td></tr>
          <tr><td><strong>Conteneur</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-cond">Conditionnel</span></td><td>Sélectionnez le conteneur reçu s'il provient d'un BL.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Remarques (état de la marchandise, écarts, etc.).</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Enregistrer une entrée en stock</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Entrée en stock</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Produit</em> dans la liste déroulante.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Saisissez la <em>Quantité (tonne)</em> et la <em>Quantité (unités)</em> constatées à la réception.</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Si l'entrée est liée à un conteneur, sélectionnez-le dans le champ <em>Conteneur</em>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">6</div>
        <p>Ajoutez une observation si nécessaire, puis cliquez sur <span class="btn-ref">Soumettre</span>.</p>
      </div>
    </div>

    <!-- ===== SORTIE ===== -->
    <h3 class="sub-title">Sortie de produit du stock</h3>
    <p class="section-text">
      Enregistrez les sorties de produits (transferts, livraisons…). Les quantités sont déduites du stock du magasin.
    </p>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr><th>Champ</th><th>Type</th><th>Obligatoire</th><th>Ce qu'il faut saisir</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Magasin</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le magasin d'où sort le produit.</td></tr>
          <tr><td><strong>Produit</strong></td><td>Liste déroulante</td><td><span class="required-badge badge-yes">Oui</span></td><td>Le produit concerné.</td></tr>
          <tr><td><strong>Quantité disponible</strong></td><td>Nombre (désactivé)</td><td>—</td><td>Affiché automatiquement. Quantité restante en stock.</td></tr>
          <tr><td><strong>Quantité à sortir</strong></td><td>Nombre</td><td><span class="required-badge badge-yes">Oui</span></td><td>Quantité à retirer du stock.</td></tr>
          <tr><td><strong>Observation</strong></td><td>Texte</td><td><span class="required-badge badge-no">Non</span></td><td>Motif de la sortie, destinataire, etc.</td></tr>
        </tbody>
      </table>
    </div>

    <h4 class="sub-title">Procédure : Enregistrer une sortie de stock</h4>
    <div class="steps">
      <div class="step-item">
        <div class="step-num">1</div>
        <p>Ouvrez le module <strong>Sortie de produit</strong> depuis le menu latéral.</p>
      </div>
      <div class="step-item">
        <div class="step-num">2</div>
        <p>Cliquez sur <span class="btn-ref">Ajouter</span>.</p>
      </div>
      <div class="step-item">
        <div class="step-num">3</div>
        <p>Sélectionnez le <em>Magasin</em>, puis le <em>Produit</em>. La <em>Quantité disponible</em> s'affiche automatiquement.</p>
      </div>
      <div class="step-item">
        <div class="step-num">4</div>
        <p>Saisissez la <em>Quantité à sortir</em> (ne peut pas dépasser la quantité disponible).</p>
      </div>
      <div class="step-item">
        <div class="step-num">5</div>
        <p>Cliquez sur <span class="btn-ref">Soumettre</span>. Le stock est mis à jour immédiatement.</p>
      </div>
    </div>

    <div class="info-card tip">
      <div class="card-title">
        <span class="material-icons-sharp" style="font-size:16px;color:#4caf50">lightbulb</span>
        Astuce
      </div>
      <p>
        Vérifiez toujours la <em>Quantité disponible</em> avant de soumettre une sortie. Si le stock est insuffisant, le système refusera la sortie.
      </p>
    </div>
  </div>
</template>
<script>
export default { name: "DocStocks" };
</script>
